<div class="space-y-5 p-5">

  <!-- Header -->
  <div>
    <h2 class="text-sm font-semibold text-slate-900">Execution-Plane Findings</h2>
    <p class="mt-0.5 text-xs text-slate-500">Cross-org inspection of monitoring findings from the execution plane. Read-only.</p>
  </div>

  <!-- Filters -->
  <div class="flex flex-wrap items-center gap-2">
    <select
      [(ngModel)]="filterSeverity"
      (ngModelChange)="applyFilters()"
      class="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
    >
      <option value="">All severities</option>
      <option *ngFor="let s of severities" [value]="s">{{ s }}</option>
    </select>

    <select
      [(ngModel)]="filterState"
      (ngModelChange)="applyFilters()"
      class="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
    >
      <option value="">All states</option>
      <option *ngFor="let st of states" [value]="st">{{ st }}</option>
    </select>

    <select
      [(ngModel)]="filterServiceType"
      (ngModelChange)="applyFilters()"
      class="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
    >
      <option value="">All types</option>
      <option *ngFor="let t of serviceTypes" [value]="t">{{ serviceTypeLabel(t) }}</option>
    </select>

    <input
      [(ngModel)]="filterOrgId"
      (keyup.enter)="applyFilters()"
      placeholder="Org ID…"
      class="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400 w-44"
    />

    <button
      (click)="applyFilters()"
      class="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50"
    >
      <lucide-icon name="search" [size]="11" [strokeWidth]="2"></lucide-icon>
      Apply
    </button>
  </div>

  <!-- Summary strip -->
  <div *ngIf="!loading && findings.length > 0" class="flex flex-wrap items-center gap-3 text-xs">
    <span class="text-slate-500">{{ findings.length }} loaded</span>
    <span *ngIf="counts.critical > 0" class="flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 font-semibold text-red-700">
      <lucide-icon name="triangle-alert" [size]="10" [strokeWidth]="2.5"></lucide-icon>
      {{ counts.critical }} CRITICAL
    </span>
    <span *ngIf="counts.high > 0" class="flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 font-semibold text-orange-700">
      {{ counts.high }} HIGH
    </span>
    <span *ngIf="counts.medium > 0" class="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 font-semibold text-amber-700">
      {{ counts.medium }} MEDIUM
    </span>
    <span class="text-slate-400">·</span>
    <span class="text-slate-600">{{ counts.open }} open</span>
    <span class="text-slate-400">{{ counts.resolved }} resolved</span>
  </div>

  <!-- Loading -->
  <div *ngIf="loading" class="flex items-center gap-2 text-sm text-slate-400">
    <lucide-icon name="loader" [size]="14" class="animate-spin" [strokeWidth]="2"></lucide-icon>
    Loading findings…
  </div>

  <!-- Error -->
  <div *ngIf="error" class="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
    <lucide-icon name="triangle-alert" [size]="14" [strokeWidth]="2"></lucide-icon>
    {{ error }}
  </div>

  <!-- Empty -->
  <div
    *ngIf="!loading && !error && findings.length === 0"
    class="rounded-xl border border-slate-200 bg-white py-14 text-center"
  >
    <lucide-icon name="shield" [size]="28" class="mx-auto text-slate-300" [strokeWidth]="1.25"></lucide-icon>
    <p class="mt-3 text-sm font-medium text-slate-500">No findings match the current filters</p>
    <p class="mt-1 text-xs text-slate-400">Try broadening the severity or state filters.</p>
  </div>

  <!-- Table -->
  <div *ngIf="!loading && findings.length > 0" class="overflow-hidden rounded-xl border border-slate-200 bg-white">
    <table class="w-full text-left text-xs">
      <thead>
        <tr class="border-b border-slate-100 bg-slate-50 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          <th class="px-4 py-2.5">Severity</th>
          <th class="px-4 py-2.5">State</th>
          <th class="px-4 py-2.5">Type</th>
          <th class="px-4 py-2.5">Title</th>
          <th class="px-4 py-2.5">Resource / Scope</th>
          <th class="px-4 py-2.5">Org</th>
          <th class="px-4 py-2.5">Observed</th>
          <th class="w-8 px-4 py-2.5"></th>
        </tr>
      </thead>
      <tbody>
        <tr
          *ngFor="let f of findings"
          class="cursor-pointer border-b border-slate-100 transition-colors last:border-0 hover:bg-slate-50"
          (click)="openDrawer(f)"
        >
          <td class="px-4 py-2.5">
            <span class="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold" [class]="severityClass(f.severity)">
              {{ f.severity }}
            </span>
          </td>
          <td class="px-4 py-2.5">
            <span class="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium" [class]="stateClass(f.findingState)">
              {{ f.findingState }}
            </span>
          </td>
          <td class="px-4 py-2.5">
            <span class="inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium" [class]="serviceTypeClass(f.serviceType)">
              {{ serviceTypeLabel(f.serviceType) }}
            </span>
          </td>
          <td class="px-4 py-2.5">
            <p class="max-w-[220px] truncate font-medium text-slate-800">{{ f.title }}</p>
            <p class="max-w-[220px] truncate text-[10px] text-slate-400">{{ f.findingType }}</p>
          </td>
          <td class="px-4 py-2.5">
            <p class="max-w-[180px] truncate font-mono text-[10px] text-slate-600">{{ f.resourceId }}</p>
            <p *ngIf="scopeLabel(f)" class="max-w-[180px] truncate font-mono text-[10px] text-slate-400">{{ scopeLabel(f) }}</p>
          </td>
          <td class="px-4 py-2.5">
            <p class="max-w-[120px] truncate text-slate-700">{{ f.orgName ?? '—' }}</p>
            <p *ngIf="f.orgId" class="font-mono text-[10px] text-slate-400">{{ f.orgId }}</p>
          </td>
          <td class="px-4 py-2.5 text-slate-500">
            {{ f.observedAt | date:'MMM d, HH:mm' }}
          </td>
          <td class="px-4 py-2.5 text-slate-300">
            <lucide-icon name="chevron-right" [size]="12" [strokeWidth]="2"></lucide-icon>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Load more -->
  <div *ngIf="!loading && nextCursor" class="flex justify-center">
    <button
      (click)="loadMore()"
      [disabled]="loadingMore"
      class="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-600 shadow-sm hover:bg-slate-50 disabled:opacity-50"
    >
      <lucide-icon *ngIf="loadingMore" name="loader" [size]="12" class="animate-spin" [strokeWidth]="2"></lucide-icon>
      {{ loadingMore ? 'Loading…' : 'Load more' }}
    </button>
  </div>

</div>

<!-- ── Detail drawer ───────────────────────────────────────────────────────── -->
<div
  class="fixed inset-0 z-50"
  [class.pointer-events-none]="!selectedFinding && !drawerLoading"
  (click)="closeDrawer()"
>
  <div
    class="absolute inset-0 bg-black/30 backdrop-blur-[1px] transition-opacity duration-200"
    [class.opacity-0]="!selectedFinding && !drawerLoading"
    [class.opacity-100]="!!selectedFinding || drawerLoading"
  ></div>
  <div
    class="absolute inset-y-0 right-0 flex w-[480px] flex-col bg-white shadow-2xl transition-transform duration-200"
    [class.translate-x-full]="!selectedFinding && !drawerLoading"
    [class.translate-x-0]="!!selectedFinding || drawerLoading"
    (click)="$event.stopPropagation()"
  >
    <div class="flex flex-shrink-0 items-center justify-between border-b border-zinc-200 px-5 py-4">
      <p class="text-sm font-semibold text-zinc-900">Finding detail</p>
      <button (click)="closeDrawer()" class="flex h-7 w-7 items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600">
        <lucide-icon name="circle-x" [size]="16" [strokeWidth]="1.75"></lucide-icon>
      </button>
    </div>

    <!-- Drawer loading -->
    <div *ngIf="drawerLoading && !selectedFinding" class="flex flex-1 items-center justify-center gap-2 text-sm text-slate-400">
      <lucide-icon name="loader" [size]="16" class="animate-spin" [strokeWidth]="2"></lucide-icon>
      Loading…
    </div>

    <!-- Drawer content -->
    <div *ngIf="selectedFinding" class="flex-1 overflow-y-auto px-5 py-5">

      <!-- Severity + state badges -->
      <div class="mb-4 flex items-center gap-2">
        <span class="inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-bold" [class]="severityClass(selectedFinding.severity)">
          {{ selectedFinding.severity }}
        </span>
        <span class="inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-medium" [class]="stateClass(selectedFinding.findingState)">
          {{ selectedFinding.findingState }}
        </span>
        <span class="inline-flex items-center rounded border px-2 py-0.5 text-[10px] font-medium" [class]="serviceTypeClass(selectedFinding.serviceType)">
          {{ serviceTypeLabel(selectedFinding.serviceType) }}
        </span>
      </div>

      <!-- Title / summary -->
      <p class="text-sm font-semibold text-zinc-900">{{ selectedFinding.title }}</p>
      <p class="mt-1 text-xs text-zinc-500">{{ selectedFinding.summary }}</p>

      <!-- Key-value pairs -->
      <div class="mt-5 space-y-3 text-xs">

        <div class="flex items-start justify-between gap-4">
          <span class="shrink-0 text-zinc-500">Finding type</span>
          <span class="break-all text-right font-mono text-zinc-700">{{ selectedFinding.findingType }}</span>
        </div>

        <div class="flex items-start justify-between gap-4">
          <span class="shrink-0 text-zinc-500">Resource</span>
          <span class="break-all text-right font-mono text-zinc-700">{{ selectedFinding.resourceId }}</span>
        </div>

        <div class="flex items-start justify-between gap-4">
          <span class="shrink-0 text-zinc-500">{{ accountLabel(selectedFinding) }}</span>
          <span class="break-all text-right font-mono text-zinc-700">{{ selectedFinding.accountId }}</span>
        </div>

        <div class="flex items-start justify-between gap-4">
          <span class="shrink-0 text-zinc-500">Region</span>
          <span class="text-right font-mono text-zinc-700">{{ selectedFinding.region ?? '—' }}</span>
        </div>

        <div class="flex items-start justify-between gap-4">
          <span class="shrink-0 text-zinc-500">Source</span>
          <span class="text-right text-zinc-700">{{ selectedFinding.source }}</span>
        </div>

        <div class="flex items-start justify-between gap-4">
          <span class="shrink-0 text-zinc-500">Organization</span>
          <span class="text-right text-zinc-700">{{ selectedFinding.orgName ?? '—' }}</span>
        </div>

        <div *ngIf="selectedFinding.orgId" class="flex items-start justify-between gap-4">
          <span class="shrink-0 text-zinc-500">Org ID</span>
          <span class="break-all text-right font-mono text-[10px] text-zinc-400">{{ selectedFinding.orgId }}</span>
        </div>

        <div class="flex items-start justify-between gap-4">
          <span class="shrink-0 text-zinc-500">Fingerprint</span>
          <span class="text-right font-mono text-[10px] text-zinc-400">{{ selectedFinding.fingerprint }}</span>
        </div>

        <div class="flex items-start justify-between gap-4">
          <span class="shrink-0 text-zinc-500">Observed</span>
          <span class="text-right text-zinc-700">{{ selectedFinding.observedAt | date:'MMM d, y HH:mm:ss' }}</span>
        </div>

        <div class="flex items-start justify-between gap-4">
          <span class="shrink-0 text-zinc-500">Created</span>
          <span class="text-right text-zinc-700">{{ selectedFinding.createdAt | date:'MMM d, y HH:mm:ss' }}</span>
        </div>

        <div *ngIf="selectedFinding.resolvedAt" class="flex items-start justify-between gap-4">
          <span class="shrink-0 text-zinc-500">Resolved</span>
          <span class="text-right text-zinc-700">{{ selectedFinding.resolvedAt | date:'MMM d, y HH:mm:ss' }}</span>
        </div>

        <div class="flex items-start justify-between gap-4">
          <span class="shrink-0 text-zinc-500">Config ID</span>
          <span class="break-all text-right font-mono text-[10px] text-zinc-400">{{ selectedFinding.configId }}</span>
        </div>

        <div class="border-t border-zinc-100 pt-2">
          <span class="font-mono text-[10px] text-zinc-300">{{ selectedFinding.findingId }}</span>
        </div>
      </div>

      <!-- Details payload -->
      <div *ngIf="selectedFinding.details && objectKeys(selectedFinding.details).length > 0" class="mt-5">
        <p class="mb-2 text-[10px] font-semibold uppercase tracking-wide text-zinc-400">Details payload</p>
        <pre class="overflow-x-auto rounded-lg border border-slate-200 bg-slate-50 p-3 font-mono text-[10px] text-slate-700 whitespace-pre-wrap break-all">{{ detailsJson(selectedFinding.details) }}</pre>
      </div>

    </div>
  </div>
</div>
